/** @format */

import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
	IContribution,
	IContributionCycle,
	IContributionCycleByIdResult,
	IContributionCyclePayload,
	IContributionPayload,
	IContributionStatus,
	IMemberContributionStatus,
} from "../../utility/types/contributionType";
import {
	createContribution,
	endCycle,
	getContributionChartData,
	getContributionCycleById,
	getContributionStatus,
	getCurrentCycleById,
	getCurrentCycleOfCircle,
	getMembersContributionStatusOfCircleCycle,
	payMissedContributions,
	startCycle,
	updateContributionChartRealTime,
} from "../actions/contributionAction";
import {
	// Admin Contribution Report Actions
	getAllContributionsAsAdmin,
	getContributionsByDateAsAdmin,
	getContributionsByUserAsAdmin,
	getContributionsByCircleAsAdmin,
	getContributionsByCycleAsAdmin,
	getFailedContributionsAsAdmin,
	// Cycle-Based Contribution Tracking Actions
	getAllActiveCyclesAsAdmin,
	getCycleContributionDetailsAsAdmin,
	getCycleSessionsBreakdownAsAdmin,
	// Auto-Pay Status Actions
	getAllUsersAutoPayStatusAsAdmin,
	getUserAutoPayStatusAsAdmin,
	getAllCirclesAutoPayStatusAsAdmin,
	getCircleAutoPayStatusAsAdmin,
	// Types
	ContributionReportEntry,
	ContributionReportSummary,
	ContributionReportPagination,
	ActiveCycleInfo,
	CycleMemberContributionStatus,
	CycleSessionContributions,
	UserAutoPayStatus,
	CircleAutoPaySummary,
} from "../actions/adminContributionAction";

export interface ContributionState {
	contributionPayload: IContributionPayload | null;
	contribution: IContribution | null;
	contributionStatus: { [key: string]: IContributionStatus };
	contributionCyclePayload: IContributionCyclePayload | null;
	contributionCycle: IContributionCycle | null;
	contributionCycleByCircle: { [unionId: string]: IContributionCycle | null };
	contributionCycleByIdResult: {
		[unionId: string]: IContributionCycleByIdResult | null;
	};
	memberContributionStatus?: IMemberContributionStatus | null;
	memberContributionStatusByUnion: {
		[unionId: string]: IMemberContributionStatus | null;
	};
	chartData: {
		[unionId: string]: {
			data: any;
			period: any;
			lastUpdated: any;
		};
	};
	realTimeUpdates: {
		[unionId: string]: {
			stats: any;
			newContribution?: any;
			timeStamp: any;
		};
	};
	// Admin Contribution Reports State
	adminContributions: {
		contributions: ContributionReportEntry[];
		pagination: ContributionReportPagination | null;
		summary: ContributionReportSummary | null;
		isLoading: boolean;
		error: string;
	};
	adminContributionsByDate: {
		date: string | null;
		contributions: ContributionReportEntry[];
		pagination: ContributionReportPagination | null;
		summary: ContributionReportSummary | null;
		isLoading: boolean;
		error: string;
	};
	adminContributionsByUser: {
		userId: string | null;
		userName: string | null;
		contributions: ContributionReportEntry[];
		pagination: ContributionReportPagination | null;
		summary: ContributionReportSummary | null;
		isLoading: boolean;
		error: string;
	};
	adminContributionsByCircle: {
		circleId: string | null;
		circleName: string | null;
		contributions: ContributionReportEntry[];
		pagination: ContributionReportPagination | null;
		summary: ContributionReportSummary | null;
		isLoading: boolean;
		error: string;
	};
	adminContributionsByCycle: {
		cycleId: string | null;
		cycle: ActiveCycleInfo | null;
		contributions: ContributionReportEntry[];
		pagination: ContributionReportPagination | null;
		summary: ContributionReportSummary | null;
		isLoading: boolean;
		error: string;
	};
	adminFailedContributions: {
		contributions: ContributionReportEntry[];
		pagination: ContributionReportPagination | null;
		failureAnalysis: any;
		isLoading: boolean;
		error: string;
	};
	// Cycle-Based Contribution Tracking State
	adminActiveCycles: {
		cycles: ActiveCycleInfo[];
		pagination: ContributionReportPagination | null;
		summary: {
			totalActiveCycles: number;
			totalExpectedContributions: number;
			totalActualContributions: number;
		} | null;
		isLoading: boolean;
		error: string;
	};
	adminCycleContributionDetails: {
		cycleId: string | null;
		cycle: ActiveCycleInfo | null;
		memberContributions: CycleMemberContributionStatus[];
		summary: any;
		isLoading: boolean;
		error: string;
	};
	adminCycleSessionsBreakdown: {
		cycleId: string | null;
		cycle: ActiveCycleInfo | null;
		sessions: CycleSessionContributions[];
		summary: any;
		isLoading: boolean;
		error: string;
	};
	// Auto-Pay Status State
	adminUsersAutoPayStatus: {
		users: UserAutoPayStatus[];
		pagination: ContributionReportPagination | null;
		summary: any;
		isLoading: boolean;
		error: string;
	};
	adminUserAutoPayStatus: {
		userId: string | null;
		user: UserAutoPayStatus | null;
		isLoading: boolean;
		error: string;
	};
	adminCirclesAutoPayStatus: {
		circles: CircleAutoPaySummary[];
		pagination: ContributionReportPagination | null;
		summary: any;
		isLoading: boolean;
		error: string;
	};
	adminCircleAutoPayStatus: {
		circleId: string | null;
		circle: CircleAutoPaySummary | null;
		members: any[];
		isLoading: boolean;
		error: string;
	};
	isLoading: boolean;
	error: string;
}

interface ContributionStatusPayload {
	contributionId: string;
	status: string;
}

const initialState: ContributionState = {
	contributionPayload: null,
	contribution: null,
	contributionStatus: {},
	contributionCyclePayload: null,
	contributionCycle: null,
	contributionCycleByCircle: {},
	contributionCycleByIdResult: {},
	memberContributionStatus: null,
	memberContributionStatusByUnion: {},
	chartData: {},
	realTimeUpdates: {},
	// Admin Contribution Reports Initial State
	adminContributions: {
		contributions: [],
		pagination: null,
		summary: null,
		isLoading: false,
		error: "",
	},
	adminContributionsByDate: {
		date: null,
		contributions: [],
		pagination: null,
		summary: null,
		isLoading: false,
		error: "",
	},
	adminContributionsByUser: {
		userId: null,
		userName: null,
		contributions: [],
		pagination: null,
		summary: null,
		isLoading: false,
		error: "",
	},
	adminContributionsByCircle: {
		circleId: null,
		circleName: null,
		contributions: [],
		pagination: null,
		summary: null,
		isLoading: false,
		error: "",
	},
	adminContributionsByCycle: {
		cycleId: null,
		cycle: null,
		contributions: [],
		pagination: null,
		summary: null,
		isLoading: false,
		error: "",
	},
	adminFailedContributions: {
		contributions: [],
		pagination: null,
		failureAnalysis: null,
		isLoading: false,
		error: "",
	},
	// Cycle-Based Contribution Tracking Initial State
	adminActiveCycles: {
		cycles: [],
		pagination: null,
		summary: null,
		isLoading: false,
		error: "",
	},
	adminCycleContributionDetails: {
		cycleId: null,
		cycle: null,
		memberContributions: [],
		summary: null,
		isLoading: false,
		error: "",
	},
	adminCycleSessionsBreakdown: {
		cycleId: null,
		cycle: null,
		sessions: [],
		summary: null,
		isLoading: false,
		error: "",
	},
	// Auto-Pay Status Initial State
	adminUsersAutoPayStatus: {
		users: [],
		pagination: null,
		summary: null,
		isLoading: false,
		error: "",
	},
	adminUserAutoPayStatus: {
		userId: null,
		user: null,
		isLoading: false,
		error: "",
	},
	adminCirclesAutoPayStatus: {
		circles: [],
		pagination: null,
		summary: null,
		isLoading: false,
		error: "",
	},
	adminCircleAutoPayStatus: {
		circleId: null,
		circle: null,
		members: [],
		isLoading: false,
		error: "",
	},
	isLoading: false,
	error: "",
};

const contributionSlice = createSlice({
	name: "contribution",
	initialState,
	reducers: {
		updateContributionStatus: (
			state,
			action: PayloadAction<ContributionStatusPayload>,
		) => {
			if (state.contribution) {
				state.contribution.status = action.payload.status;
			}
		},
		clearContributionCycle: (state) => {
			state.contributionCycle = null;
		},
		// Add a reducer to ensure state structure integrity
		ensureStateStructure: (state) => {
			if (!state.chartData) {
				state.chartData = {};
			}
			if (!state.realTimeUpdates) {
				state.realTimeUpdates = {};
			}
			if (!state.contributionStatus) {
				state.contributionStatus = {};
			}
			if (!state.contributionCycleByCircle) {
				state.contributionCycleByCircle = {};
			}
			if (!state.contributionCycleByIdResult) {
				state.contributionCycleByIdResult = {};
			}
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getCurrentCycleOfCircle.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(getCurrentCycleOfCircle.fulfilled, (state, action) => {
			state.isLoading = false;
			const unionId = action.meta.arg.unionId;
			if (!state.contributionCycleByCircle) {
				state.contributionCycleByCircle = {};
			}
			if (action.payload && action.payload._id) {
				state.contributionCycleByCircle[unionId] = action.payload;
			} else {
				state.contributionCycleByCircle[unionId] = null;
			}
		});

		builder.addCase(getCurrentCycleOfCircle.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload == "string" ?
					action.payload
				:	"Error. Failed to get current cycle of circle";
			state.contributionCycle = null;
		});

		builder.addCase(createContribution.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(createContribution.fulfilled, (state, action) => {
			state.isLoading = false;
			state.contribution = action.payload;

			// 🔒 DUPLICATE PREVENTION: Immediately update contribution status to prevent double contributions
			// When a contribution is successful, mark hasContributedToday as true in the store
			const contribution = action.payload;
			if (contribution && contribution.unionId && contribution.memberId) {
				const unionId =
					typeof contribution.unionId === "object" ?
						contribution.unionId._id
					:	contribution.unionId;
				const memberId =
					typeof contribution.memberId === "object" ?
						contribution.memberId._id
					:	contribution.memberId;
				const key = `${unionId}-${memberId}`;
				state.contributionStatus[key] = { hasContributedToday: true };
			}
		});

		builder.addCase(createContribution.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload == "string" ?
					action.payload
				:	"Error. Failed to contribute";
		});

		// 🔒 DUPLICATE PREVENTION: Handle missed contributions payment
		builder.addCase(payMissedContributions.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(payMissedContributions.fulfilled, (state, action) => {
			state.isLoading = false;
			// When missed contributions are paid successfully, update the contribution status
			// if today was included in the missed dates
			const response = action.payload;
			if (response?.success && response?.contributions?.length > 0) {
				const firstContrib = response.contributions[0];
				if (firstContrib?.unionId && firstContrib?.memberId) {
					const unionId =
						typeof firstContrib.unionId === "object" ?
							firstContrib.unionId._id
						:	firstContrib.unionId;
					const memberId =
						typeof firstContrib.memberId === "object" ?
							firstContrib.memberId._id
						:	firstContrib.memberId;

					// Check if today's date was in the paid dates
					const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
					const paidDates = response.contributions.map(
						(c: any) => c.contributionForDate,
					);
					if (paidDates.includes(today)) {
						const key = `${unionId}-${memberId}`;
						state.contributionStatus[key] = { hasContributedToday: true };
					}
				}
			}
		});

		builder.addCase(payMissedContributions.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload == "string" ?
					action.payload
				:	"Error. Failed to pay missed contributions";
		});

		builder.addCase(getContributionStatus.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(getContributionStatus.fulfilled, (state, action) => {
			state.isLoading = false;
			const {
				unionId,
				memberId,
				hasContributedToday,
				isMidCycleJoiner,
				eligibleToContributeFrom,
				canContributeYet,
			} = action.payload;

			const key = `${unionId}-${memberId}`;
			state.contributionStatus[key] = {
				hasContributedToday,
				isMidCycleJoiner,
				eligibleToContributeFrom,
				canContributeYet,
			};
		});

		builder.addCase(getContributionStatus.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload == "string" ?
					action.payload
				:	"Error. Failed to fetch contribution status";
		});

		builder.addCase(startCycle.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(startCycle.fulfilled, (state, action) => {
			state.isLoading = false;
			state.contributionCycle = action.payload;
		});

		builder.addCase(startCycle.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload == "string" ?
					action.payload
				:	"Error. Failed to start cycle";
		});

		builder.addCase(endCycle.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(endCycle.fulfilled, (state, action) => {
			state.isLoading = false;
			if (
				action.payload.message &&
				action.payload.cycle &&
				action.payload.cycle._id
			) {
				state.contributionCycle = action.payload;
			}
		});

		builder.addCase(endCycle.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload == "string" ?
					action.payload
				:	"Error. Failed to end cycle";
		});

		builder.addCase(getCurrentCycleById.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(getCurrentCycleById.fulfilled, (state, action) => {
			state.isLoading = false;
			const unionId = action.meta.arg.unionId;
			// if (!state.contributionCycleByIdResult) {
			//   state.contributionCycleByIdResult = {};
			// }
			// if (action.payload && action.payload.cycle._id) {
			//   state.contributionCycleByIdResult[unionId] = action.payload;
			// } else {
			//   state.contributionCycleByIdResult[unionId] = null;
			// }
			state.contributionCycleByIdResult[unionId] = action.payload;
		});

		builder.addCase(getCurrentCycleById.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload == "string" ?
					action.payload
				:	"Failed to get current cycle ById";
			const unionId = action.meta.arg.unionId;
			state.contributionCycleByCircle[unionId] = null;
		});

		builder.addCase(
			getMembersContributionStatusOfCircleCycle.pending,
			(state, action) => {
				state.isLoading = true;
				// Clear old data for this specific unionId to prevent stale data display
				const unionId = action.meta.arg.unionId;
				if (unionId) {
					state.memberContributionStatusByUnion[unionId] = null;
				}
			},
		);

		builder.addCase(
			getMembersContributionStatusOfCircleCycle.fulfilled,
			(state, action) => {
				state.isLoading = false;
				state.memberContributionStatus = action.payload;
				// Also store by unionId to prevent data mixing between circles
				const unionId = action.meta.arg.unionId;
				if (unionId) {
					state.memberContributionStatusByUnion[unionId] = action.payload;
				}
			},
		);

		builder.addCase(
			getMembersContributionStatusOfCircleCycle.rejected,
			(state, action) => {
				state.isLoading = false;
				state.error =
					typeof action.payload == "string" ?
						action.payload
					:	"Error. Failed to get member contribution statuses of current cycle of circle";
				state.contributionCycle = null;
			},
		);

		builder.addCase(getContributionCycleById.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(getContributionCycleById.fulfilled, (state, action) => {
			state.isLoading = false;
			state.contributionCycle = action.payload;
		});

		builder.addCase(getContributionCycleById.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload == "string" ?
					action.payload
				:	"Error. Failed to get  cycle of circle";
			state.contributionCycle = null;
		});

		builder.addCase(getContributionChartData.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(getContributionChartData.fulfilled, (state, action) => {
			const { unionId, data, period, lastUpdated } = action.payload;
			// Ensure chartData object exists
			if (!state.chartData) {
				state.chartData = {};
			}
			state.chartData[unionId] = {
				data,
				period,
				lastUpdated,
			};
		});

		builder.addCase(getContributionChartData.rejected, (state, action) => {
			state.isLoading = false;
			state.error =
				typeof action.payload == "string" ?
					action.payload
				:	"Error. Failed to get  contribution chart data";
		});

		builder.addCase(updateContributionChartRealTime.pending, (state) => {
			// Don't set loading for real-time updates to avoid UI flickering
		});

		builder.addCase(
			updateContributionChartRealTime.fulfilled,
			(state, action) => {
				const { unionId, contributionStats, newContribution, timeStamp } =
					action.payload;

				// Ensure realTimeUpdates object exists
				if (!state.realTimeUpdates) {
					state.realTimeUpdates = {};
				}
				state.realTimeUpdates[unionId] = {
					stats: contributionStats,
					newContribution,
					timeStamp,
				};

				// Ensure chartData object exists and update if present
				if (!state.chartData) {
					state.chartData = {};
				}
				if (state.chartData[unionId]) {
					state.chartData[unionId].lastUpdated = timeStamp;
				}
			},
		);

		builder.addCase(
			updateContributionChartRealTime.rejected,
			(state, action) => {
				// Silent failure for real-time updates
				console.warn("Real-time chart update failed:", action.payload);
			},
		);

		// ==================== Admin Contribution Reports ====================

		// Get All Contributions
		builder.addCase(getAllContributionsAsAdmin.pending, (state) => {
			state.adminContributions.isLoading = true;
			state.adminContributions.error = "";
		});

		builder.addCase(getAllContributionsAsAdmin.fulfilled, (state, action) => {
			state.adminContributions.isLoading = false;
			state.adminContributions.contributions = action.payload.data || [];
			state.adminContributions.pagination = action.payload.pagination || null;
			state.adminContributions.summary = action.payload.summary || null;
		});

		builder.addCase(getAllContributionsAsAdmin.rejected, (state, action) => {
			state.adminContributions.isLoading = false;
			state.adminContributions.error =
				typeof action.payload === "string" ?
					action.payload
				:	"Failed to fetch contributions";
		});

		// Get Contributions By Date
		builder.addCase(getContributionsByDateAsAdmin.pending, (state) => {
			state.adminContributionsByDate.isLoading = true;
			state.adminContributionsByDate.error = "";
		});

		builder.addCase(
			getContributionsByDateAsAdmin.fulfilled,
			(state, action) => {
				state.adminContributionsByDate.isLoading = false;
				state.adminContributionsByDate.date = action.payload.date || null;
				state.adminContributionsByDate.contributions =
					action.payload.data || [];
				state.adminContributionsByDate.pagination =
					action.payload.pagination || null;
				state.adminContributionsByDate.summary = action.payload.summary || null;
			},
		);

		builder.addCase(getContributionsByDateAsAdmin.rejected, (state, action) => {
			state.adminContributionsByDate.isLoading = false;
			state.adminContributionsByDate.error =
				typeof action.payload === "string" ?
					action.payload
				:	"Failed to fetch contributions by date";
		});

		// Get Contributions By User
		builder.addCase(getContributionsByUserAsAdmin.pending, (state) => {
			state.adminContributionsByUser.isLoading = true;
			state.adminContributionsByUser.error = "";
		});

		builder.addCase(
			getContributionsByUserAsAdmin.fulfilled,
			(state, action) => {
				state.adminContributionsByUser.isLoading = false;
				state.adminContributionsByUser.userId =
					action.payload.user?.userId || null;
				state.adminContributionsByUser.userName =
					action.payload.user?.name || null;
				state.adminContributionsByUser.contributions =
					action.payload.data || [];
				state.adminContributionsByUser.pagination =
					action.payload.pagination || null;
				state.adminContributionsByUser.summary = action.payload.summary || null;
			},
		);

		builder.addCase(getContributionsByUserAsAdmin.rejected, (state, action) => {
			state.adminContributionsByUser.isLoading = false;
			state.adminContributionsByUser.error =
				typeof action.payload === "string" ?
					action.payload
				:	"Failed to fetch contributions by user";
		});

		// Get Contributions By Circle
		builder.addCase(getContributionsByCircleAsAdmin.pending, (state) => {
			state.adminContributionsByCircle.isLoading = true;
			state.adminContributionsByCircle.error = "";
		});

		builder.addCase(
			getContributionsByCircleAsAdmin.fulfilled,
			(state, action) => {
				state.adminContributionsByCircle.isLoading = false;
				state.adminContributionsByCircle.circleId =
					action.payload.circle?.circleId || null;
				state.adminContributionsByCircle.circleName =
					action.payload.circle?.circleName || null;
				state.adminContributionsByCircle.contributions =
					action.payload.data || [];
				state.adminContributionsByCircle.pagination =
					action.payload.pagination || null;
				state.adminContributionsByCircle.summary =
					action.payload.summary || null;
			},
		);

		builder.addCase(
			getContributionsByCircleAsAdmin.rejected,
			(state, action) => {
				state.adminContributionsByCircle.isLoading = false;
				state.adminContributionsByCircle.error =
					typeof action.payload === "string" ?
						action.payload
					:	"Failed to fetch contributions by circle";
			},
		);

		// Get Contributions By Cycle
		builder.addCase(getContributionsByCycleAsAdmin.pending, (state) => {
			state.adminContributionsByCycle.isLoading = true;
			state.adminContributionsByCycle.error = "";
		});

		builder.addCase(
			getContributionsByCycleAsAdmin.fulfilled,
			(state, action) => {
				state.adminContributionsByCycle.isLoading = false;
				state.adminContributionsByCycle.cycleId =
					action.payload.cycle?.cycleId || null;
				state.adminContributionsByCycle.cycle = action.payload.cycle || null;
				state.adminContributionsByCycle.contributions =
					action.payload.data || [];
				state.adminContributionsByCycle.pagination =
					action.payload.pagination || null;
				state.adminContributionsByCycle.summary =
					action.payload.summary || null;
			},
		);

		builder.addCase(
			getContributionsByCycleAsAdmin.rejected,
			(state, action) => {
				state.adminContributionsByCycle.isLoading = false;
				state.adminContributionsByCycle.error =
					typeof action.payload === "string" ?
						action.payload
					:	"Failed to fetch contributions by cycle";
			},
		);

		// Get Failed Contributions
		builder.addCase(getFailedContributionsAsAdmin.pending, (state) => {
			state.adminFailedContributions.isLoading = true;
			state.adminFailedContributions.error = "";
		});

		builder.addCase(
			getFailedContributionsAsAdmin.fulfilled,
			(state, action) => {
				state.adminFailedContributions.isLoading = false;
				state.adminFailedContributions.contributions =
					action.payload.data || [];
				state.adminFailedContributions.pagination =
					action.payload.pagination || null;
				state.adminFailedContributions.failureAnalysis =
					action.payload.failureAnalysis || null;
			},
		);

		builder.addCase(getFailedContributionsAsAdmin.rejected, (state, action) => {
			state.adminFailedContributions.isLoading = false;
			state.adminFailedContributions.error =
				typeof action.payload === "string" ?
					action.payload
				:	"Failed to fetch failed contributions";
		});

		// ==================== Cycle-Based Contribution Tracking ====================

		// Get All Active Cycles
		builder.addCase(getAllActiveCyclesAsAdmin.pending, (state) => {
			state.adminActiveCycles.isLoading = true;
			state.adminActiveCycles.error = "";
		});

		builder.addCase(getAllActiveCyclesAsAdmin.fulfilled, (state, action) => {
			state.adminActiveCycles.isLoading = false;
			state.adminActiveCycles.cycles = action.payload.data || [];
			state.adminActiveCycles.pagination = action.payload.pagination || null;
			state.adminActiveCycles.summary = action.payload.summary || null;
		});

		builder.addCase(getAllActiveCyclesAsAdmin.rejected, (state, action) => {
			state.adminActiveCycles.isLoading = false;
			state.adminActiveCycles.error =
				typeof action.payload === "string" ?
					action.payload
				:	"Failed to fetch active cycles";
		});

		// Get Cycle Contribution Details
		builder.addCase(getCycleContributionDetailsAsAdmin.pending, (state) => {
			state.adminCycleContributionDetails.isLoading = true;
			state.adminCycleContributionDetails.error = "";
		});

		builder.addCase(
			getCycleContributionDetailsAsAdmin.fulfilled,
			(state, action) => {
				state.adminCycleContributionDetails.isLoading = false;
				state.adminCycleContributionDetails.cycleId =
					action.payload.cycle?.cycleId || null;
				state.adminCycleContributionDetails.cycle =
					action.payload.cycle || null;
				state.adminCycleContributionDetails.memberContributions =
					action.payload.memberContributions || [];
				state.adminCycleContributionDetails.summary =
					action.payload.summary || null;
			},
		);

		builder.addCase(
			getCycleContributionDetailsAsAdmin.rejected,
			(state, action) => {
				state.adminCycleContributionDetails.isLoading = false;
				state.adminCycleContributionDetails.error =
					typeof action.payload === "string" ?
						action.payload
					:	"Failed to fetch cycle contribution details";
			},
		);

		// Get Cycle Sessions Breakdown
		builder.addCase(getCycleSessionsBreakdownAsAdmin.pending, (state) => {
			state.adminCycleSessionsBreakdown.isLoading = true;
			state.adminCycleSessionsBreakdown.error = "";
		});

		builder.addCase(
			getCycleSessionsBreakdownAsAdmin.fulfilled,
			(state, action) => {
				state.adminCycleSessionsBreakdown.isLoading = false;
				state.adminCycleSessionsBreakdown.cycleId =
					action.payload.cycle?.cycleId || null;
				state.adminCycleSessionsBreakdown.cycle = action.payload.cycle || null;
				state.adminCycleSessionsBreakdown.sessions =
					action.payload.sessions || [];
				state.adminCycleSessionsBreakdown.summary =
					action.payload.summary || null;
			},
		);

		builder.addCase(
			getCycleSessionsBreakdownAsAdmin.rejected,
			(state, action) => {
				state.adminCycleSessionsBreakdown.isLoading = false;
				state.adminCycleSessionsBreakdown.error =
					typeof action.payload === "string" ?
						action.payload
					:	"Failed to fetch cycle sessions breakdown";
			},
		);

		// ==================== Auto-Pay Status ====================

		// Get All Users Auto-Pay Status
		builder.addCase(getAllUsersAutoPayStatusAsAdmin.pending, (state) => {
			state.adminUsersAutoPayStatus.isLoading = true;
			state.adminUsersAutoPayStatus.error = "";
		});

		builder.addCase(
			getAllUsersAutoPayStatusAsAdmin.fulfilled,
			(state, action) => {
				state.adminUsersAutoPayStatus.isLoading = false;
				state.adminUsersAutoPayStatus.users = action.payload.data || [];
				state.adminUsersAutoPayStatus.pagination =
					action.payload.pagination || null;
				state.adminUsersAutoPayStatus.summary = action.payload.summary || null;
			},
		);

		builder.addCase(
			getAllUsersAutoPayStatusAsAdmin.rejected,
			(state, action) => {
				state.adminUsersAutoPayStatus.isLoading = false;
				state.adminUsersAutoPayStatus.error =
					typeof action.payload === "string" ?
						action.payload
					:	"Failed to fetch users auto-pay status";
			},
		);

		// Get User Auto-Pay Status
		builder.addCase(getUserAutoPayStatusAsAdmin.pending, (state) => {
			state.adminUserAutoPayStatus.isLoading = true;
			state.adminUserAutoPayStatus.error = "";
		});

		builder.addCase(getUserAutoPayStatusAsAdmin.fulfilled, (state, action) => {
			state.adminUserAutoPayStatus.isLoading = false;
			state.adminUserAutoPayStatus.userId = action.payload.user?.userId || null;
			state.adminUserAutoPayStatus.user = action.payload.user || null;
		});

		builder.addCase(getUserAutoPayStatusAsAdmin.rejected, (state, action) => {
			state.adminUserAutoPayStatus.isLoading = false;
			state.adminUserAutoPayStatus.error =
				typeof action.payload === "string" ?
					action.payload
				:	"Failed to fetch user auto-pay status";
		});

		// Get All Circles Auto-Pay Status
		builder.addCase(getAllCirclesAutoPayStatusAsAdmin.pending, (state) => {
			state.adminCirclesAutoPayStatus.isLoading = true;
			state.adminCirclesAutoPayStatus.error = "";
		});

		builder.addCase(
			getAllCirclesAutoPayStatusAsAdmin.fulfilled,
			(state, action) => {
				state.adminCirclesAutoPayStatus.isLoading = false;
				state.adminCirclesAutoPayStatus.circles = action.payload.data || [];
				state.adminCirclesAutoPayStatus.pagination =
					action.payload.pagination || null;
				state.adminCirclesAutoPayStatus.summary =
					action.payload.summary || null;
			},
		);

		builder.addCase(
			getAllCirclesAutoPayStatusAsAdmin.rejected,
			(state, action) => {
				state.adminCirclesAutoPayStatus.isLoading = false;
				state.adminCirclesAutoPayStatus.error =
					typeof action.payload === "string" ?
						action.payload
					:	"Failed to fetch circles auto-pay status";
			},
		);

		// Get Circle Auto-Pay Status
		builder.addCase(getCircleAutoPayStatusAsAdmin.pending, (state) => {
			state.adminCircleAutoPayStatus.isLoading = true;
			state.adminCircleAutoPayStatus.error = "";
		});

		builder.addCase(
			getCircleAutoPayStatusAsAdmin.fulfilled,
			(state, action) => {
				state.adminCircleAutoPayStatus.isLoading = false;
				state.adminCircleAutoPayStatus.circleId =
					action.payload.circle?.circleId || null;
				state.adminCircleAutoPayStatus.circle = action.payload.circle || null;
				state.adminCircleAutoPayStatus.members = action.payload.members || [];
			},
		);

		builder.addCase(getCircleAutoPayStatusAsAdmin.rejected, (state, action) => {
			state.adminCircleAutoPayStatus.isLoading = false;
			state.adminCircleAutoPayStatus.error =
				typeof action.payload === "string" ?
					action.payload
				:	"Failed to fetch circle auto-pay status";
		});
	},
});

export const {
	updateContributionStatus,
	clearContributionCycle,
	ensureStateStructure,
} = contributionSlice.actions;

export default contributionSlice.reducer;
